import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { SpecialtyService } from '../../../services/specialty.service';
import { Specialty, SpecialtyListParams } from '../../../dto/specialty.dto';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { cpfValidator } from 'src/app/util/cpf-validator';
import { LoginService } from 'src/app/services/login.service';
import { DoctorService } from 'src/app/services/doctor.service';
import { CreateDoctorDTO, Doctor } from '../../../dto/doctor.dto';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { CepService } from 'src/app/services/cep.service';
import {
  finalize,
  debounceTime,
  distinctUntilChanged,
  map,
} from 'rxjs/operators';

@Component({
  selector: 'app-add-doctor-modal',
  templateUrl: './add-doctor-modal.component.html',
  styleUrls: ['./add-doctor-modal.component.scss'],
})
export class AddDoctorModalComponent implements OnInit, OnDestroy {
  loading = false;
  isEdition = false;
  cepValid = false;

  specialtiesListParams: SpecialtyListParams = {
    page: 1,
    limit: 1000,
    searchTerm: '',
    active: true,
    order: 'ASC',
  };

  form!: FormGroup;
  specialties: Specialty[] = [];

  missingTooltip = '';
  private statusSub?: Subscription;
  private cepSub?: Subscription;
  private lastFetchedCep = '';

  constructor(
    private dialogRef: MatDialogRef<AddDoctorModalComponent>,
    private fb: FormBuilder,
    private specialtyService: SpecialtyService,
    private loginService: LoginService,
    private doctorService: DoctorService,
    private cepService: CepService,
    @Inject(MAT_DIALOG_DATA) public data: Partial<Doctor>
  ) {}

  compareById = (a: number | string, b: number | string) =>
    a != null && b != null && Number(a) === Number(b);

  private fieldLabels: Record<string, string> = {
    cpf: 'CPF',
    name: 'Nome',
    email: 'E-mail',
    birthDate: 'Nascimento',
    mobilePhone: 'Celular',
    addressStreet: 'Rua',
    addressNumber: 'Número',
    addressPostalCode: 'CEP',
    addressProvince: 'Bairro',
    specialtyIds: 'Especialidades',
  };

  ngOnInit(): void {
    this.form = this.fb.group({
      cpf: ['', [Validators.required, cpfValidator()]],
      name: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      birthDate: [null, [Validators.required, this.birthDateValidator]],
      mobilePhone: [null, [Validators.required, this.phoneDigitsValidator]],
      addressPostalCode: [
        null,
        [Validators.required, this.cepPatternValidator],
      ],
      addressStreet: [{ value: null, disabled: true }, Validators.required],
      addressNumber: [{ value: null, disabled: true }, Validators.required],
      addressProvince: [{ value: null, disabled: true }, Validators.required],
      specialtyIds: [[], [Validators.required, Validators.minLength(1)]],
    });

    if (this.data) {
      this.isEdition = true;
      const selectedIds =
        Array.isArray(this.data.specialties) && this.data.specialties.length
          ? this.data.specialties.map((s) =>
              Number((s as any).id ?? (s as any).specialtyId)
            )
          : [];

      this.form.patchValue(
        {
          cpf: this.data.cpf ?? '',
          name: this.data.name ?? '',
          email: this.data.email ?? '',
          birthDate: this.data.birthDate
            ? moment(this.data.birthDate).format('DD/MM/YYYY')
            : '',
          mobilePhone: this.data.mobilePhone ?? '',
          addressStreet: this.data.addressStreet ?? '',
          addressNumber: this.data.addressNumber ?? '',
          addressPostalCode: this.data.addressPostalCode ?? '',
          addressProvince: this.data.addressProvince ?? '',
          specialtyIds: selectedIds,
        },
        { emitEvent: false }
      );

      this.form.get('cpf')?.disable({ emitEvent: false });
      const initialCep = (
        this.form.get('addressPostalCode')?.value || ''
      ).toString();
      if (this.isCepFormatValid(initialCep)) {
        this.enableAddressFields();
        this.cepValid = true;
        this.lastFetchedCep = initialCep.replace(/\D/g, '');
      }
    }

    this.statusSub = this.form.statusChanges.subscribe(() =>
      this.updateTooltip()
    );

    this.cepSub = this.form
      .get('addressPostalCode')!
      .valueChanges.pipe(
        map((v: string) => (v || '').replace(/\D/g, '')),
        debounceTime(120),
        distinctUntilChanged()
      )
      .subscribe((digits) => {
        if (digits.length === 8 && digits !== this.lastFetchedCep) {
          this.fetchCep(digits);
        }
        if (digits.length < 8) {
          this.cepValid = false;
          this.form.get('addressPostalCode')?.setErrors({ invalidCep: true });
          this.disableAddressFields();
        }
      });

    this.updateTooltip();
    this.getSpecialties();
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe();
    this.cepSub?.unsubscribe();
  }

  getSpecialties() {
    this.loading = true;
    this.specialtyService
      .findAll(this.specialtiesListParams)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.specialties = (response?.list ?? []).map((s) => ({
            ...s,
            id: Number(s.id),
          }));
          const current = this.form.get('specialtyIds')?.value as
            | (number | string)[]
            | null;
          if (
            (!current || current.length === 0) &&
            Array.isArray(this.data?.specialties)
          ) {
            const ids = this.data!.specialties.map((s) =>
              Number((s as any).id ?? (s as any).specialtyId)
            );
            if (ids.length) {
              this.form
                .get('specialtyIds')
                ?.setValue(ids, { emitEvent: false });
              this.form
                .get('specialtyIds')
                ?.updateValueAndValidity({ emitEvent: false });
              this.updateTooltip();
            }
          }
        },
        error: () => {
          this.specialties = [];
          this.loginService.showMessage(
            'Falha ao carregar especialidades',
            3000
          );
        },
      });
  }

  onChangeEmail() {
    if (
      !this.loginService.isEmailPatternValid(this.form.controls.email.value)
    ) {
      this.form.controls.email.setErrors({ invalid: true });
    }
  }

  async onChangeCPF() {
    if (this.isEdition) return;
    const cpf = this.form.controls.cpf.value;
    if (!cpf) {
      this.form.controls.cpf.setErrors({ required: true });
      return this.updateTooltip();
    }
    if (!this.loginService.isCpfPatternValid(cpf)) {
      this.form.controls.cpf.setErrors({ invalid: true });
      return this.updateTooltip();
    }
    const exists = await this.doctorService.findByCpf(cpf).toPromise();
    if (exists && (!this.isEdition || exists.id !== this.data?.id)) {
      this.form.controls.cpf.setErrors({ alreadyExist: true });
      return this.updateTooltip();
    }
    this.form.controls.cpf.setErrors(null);
    this.updateTooltip();
  }

  onCepBlur() {
    const raw = (this.form.get('addressPostalCode')?.value || '') as string;
    const clean = raw.replace(/\D/g, '');
    if (clean.length === 8) {
      this.fetchCep(clean);
    }
  }

  private fetchCep(clean: string) {
    this.loading = true;
    this.cepService
      .getAddressByZipCode(clean)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (addr) => {
          this.cepValid = true;
          this.lastFetchedCep = clean;
          this.form.get('addressPostalCode')?.setErrors(null);
          this.enableAddressFields();
          this.form.get('addressStreet')?.setValue(addr.logradouro || '');
          this.form.get('addressProvince')?.setValue(addr.bairro || '');
          this.updateTooltip();
        },
        error: () => {
          this.cepValid = false;
          this.form.get('addressPostalCode')?.setErrors({ invalidCep: true });
          this.disableAddressFields();
          this.loginService.showMessage('CEP inválido ou não encontrado', 3000);
          this.updateTooltip();
        },
      });
  }

  saveDoctor() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.updateTooltip();
      this.loginService.showMessage('Preencha todos os campos corretamente');
      return;
    }

    const payload: CreateDoctorDTO = this.form.getRawValue();
    payload.birthDate = moment(payload.birthDate, 'DD/MM/YYYY').toDate();
    if (this.isEdition && this.data?.id) payload.id = this.data.id;

    this.loading = true;
    this.dialogRef.disableClose = true;

    this.doctorService
      .save(payload)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.dialogRef.disableClose = false;
        })
      )
      .subscribe({
        next: () => {
          this.loginService.showMessage('Médico salvo com sucesso', 3000);
          this.dialogRef.close({ __saved: true });
        },
        error: (err) => {
          const msg =
            this.extractBackendMessage(err) || 'Erro ao salvar médico';
          console.error(err);
          this.loginService.showMessage(msg, 5000);
        },
      });
  }

  private extractBackendMessage(err: any): string | null {
    const m = err?.error?.message ?? err?.message;
    if (Array.isArray(m)) return m.filter(Boolean).join('\n');
    if (typeof m === 'string') return m;
    const desc =
      err?.error?.errors?.[0]?.description ??
      err?.errors?.[0]?.description ??
      null;
    return typeof desc === 'string' ? desc : null;
  }

  private birthDateValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const v = (control.value || '').trim();
    if (!v) return null;

    const digits = v.replace(/\D/g, '');
    if (digits.length < 8) return null;

    const day = Number(digits.slice(0, 2));
    const month = Number(digits.slice(2, 4));
    const year = Number(digits.slice(4, 8));

    if (month < 1 || month > 12 || day < 1 || day > 31)
      return { invalidFormat: true };

    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return { invalidFormat: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date.getTime() > today.getTime()) return { futureDate: true };

    let age = today.getFullYear() - year;
    const mm = today.getMonth() - (month - 1);
    if (mm < 0 || (mm === 0 && today.getDate() < day)) age--;

    if (age < 18) return { underage: true };
    if (age > 150) return { tooOld: true };

    return null;
  };

  private phoneDigitsValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const digits = (control.value || '').toString().replace(/\D/g, '');
    if (!digits) return null;
    if (digits.length !== 10 && digits.length !== 11)
      return { invalidPhone: true };
    return null;
  };

  private cepPatternValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const v = (control.value || '').toString();
    const clean = v.replace(/\D/g, '');
    if (clean.length === 0) return null;
    if (clean.length !== 8) return { invalidCep: true };
    return null;
  };

  private isCepFormatValid(v: string): boolean {
    const clean = (v || '').replace(/\D/g, '');
    return clean.length === 8;
  }

  private enableAddressFields() {
    this.form.get('addressStreet')?.enable({ emitEvent: false });
    this.form.get('addressNumber')?.enable({ emitEvent: false });
    this.form.get('addressProvince')?.enable({ emitEvent: false });
  }

  private disableAddressFields() {
    this.form.get('addressStreet')?.disable({ emitEvent: false });
    this.form.get('addressNumber')?.disable({ emitEvent: false });
    this.form.get('addressProvince')?.disable({ emitEvent: false });
  }

  private updateTooltip() {
    const miss: string[] = [];
    Object.keys(this.form.controls).forEach((key) => {
      const ctrl = this.form.get(key)!;
      const isDisabled = ctrl.disabled;
      const isRequired = ctrl.hasValidator?.(Validators.required) ?? false;
      const v = ctrl.value;
      const emptyArray = Array.isArray(v) && v.length === 0;
      if (
        !isDisabled &&
        isRequired &&
        (v === null || v === undefined || v === '' || emptyArray)
      ) {
        miss.push(this.fieldLabels[key] ?? key);
      }
    });

    const cpfCtrl = this.form.get('cpf');
    if (cpfCtrl && !this.isEdition) {
      if (cpfCtrl.errors?.['invalid']) miss.push('CPF inválido');
      if (cpfCtrl.errors?.['alreadyExist']) miss.push('CPF já cadastrado');
    }

    const emailCtrl = this.form.get('email');
    if (emailCtrl?.errors?.['email'] || emailCtrl?.errors?.['invalid'])
      miss.push('E-mail inválido');

    const birthCtrl = this.form.get('birthDate');
    if (birthCtrl?.errors?.['invalidFormat'])
      miss.push('Data no formato DD/MM/AAAA');
    if (birthCtrl?.errors?.['futureDate'])
      miss.push('Data de nascimento no futuro');
    if (birthCtrl?.errors?.['underage'])
      miss.push('Médico deve ter ao menos 18 anos');

    const phoneCtrl = this.form.get('mobilePhone');
    if (phoneCtrl?.errors?.['invalidPhone'])
      miss.push('Número de celular inválido');

    const cepCtrl = this.form.get('addressPostalCode');
    if (cepCtrl?.errors?.['invalidCep']) miss.push('CEP inválido');

    this.missingTooltip = miss.length
      ? `Preencha ou Corrija:\n ${Array.from(new Set(miss)).join(',\n ')}`
      : '';
  }
}
